import React, { useEffect, useState } from 'react';
import config from '../config';
import useTranslation from './useTranslation';

// Utilità per gestire la logica di ripetizione iOS
const IOSPromptManager = {
  getPromptCount() {
    return parseInt(localStorage.getItem('iosInstallPromptCount') || '0');
  },

  incrementPromptCount() {
    const count = this.getPromptCount() + 1;
    localStorage.setItem('iosInstallPromptCount', count.toString());
    return count;
  },

  getNextShowTime() {
    const nextShow = localStorage.getItem('iosInstallPromptNextShow');
    return nextShow ? parseInt(nextShow) : 0;
  },

  getDelayForAttempt(attempt) {
    const delays = {
      1: 1 * 24 * 60 * 60 * 1000,      // 1 giorno
      2: 3 * 24 * 60 * 60 * 1000,      // 3 giorni  
      3: 7 * 24 * 60 * 60 * 1000,      // 7 giorni
    };
    return delays[attempt] || 0;
  },

  setNextShow(responseType) {
    const count = this.getPromptCount();
    
    if (responseType === 'not-now') {
      if (count >= 3) {
        localStorage.setItem('iosInstallPromptDismissed', 'permanently');
      } else {
        const delay = this.getDelayForAttempt(count);
        const nextShow = Date.now() + delay;
        localStorage.setItem('iosInstallPromptNextShow', nextShow.toString());
      }
    }
  },

  canShowPrompt() {
    if (localStorage.getItem('iosInstallPromptDismissed') === 'permanently') {
      return false;
    }

    const nextShow = this.getNextShowTime();
    if (nextShow && Date.now() < nextShow) {
      return false;
    }

    const count = this.getPromptCount();
    if (count >= 3) {
      localStorage.setItem('iosInstallPromptDismissed', 'permanently');
      return false;
    }

    return true;
  },

  markPromptShown() {
    this.incrementPromptCount();
  }
};

const IOSInstallPrompt = () => {
  const { t } = useTranslation();
  const [showPrompt, setShowPrompt] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [currentImageSrc, setCurrentImageSrc] = useState(config.assets.icon);
  const [showImage, setShowImage] = useState(true);

  useEffect(() => {
    // Controlla se è un dispositivo iOS
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    
    // Controlla se l'app è già installata
    const isInstalled = window.matchMedia('(display-mode: standalone)').matches || 
                       window.navigator.standalone === true;
    
    // Verifica se il prompt può essere mostrato
    if (isIOS && !isInstalled && IOSPromptManager.canShowPrompt()) {
      // Mostra il prompt dopo un breve ritardo
      const timer = setTimeout(() => {
        setShowPrompt(true);
        IOSPromptManager.markPromptShown();
        
        // Animazione con ritardo per far scendere il pannello
        setTimeout(() => {
          setIsVisible(true);
        }, 100);
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, []);

  // Funzione per aprire la Web Share API (solo iOS)
  const handleInstallClick = async () => {
    // Controlla se è iOS
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    
    if (isIOS && navigator.share && window.location.protocol === 'https:') {
      try {
        await navigator.share({
          title: t('installaApp'),
          text: t('installaAppDescrizione'), 
          url: window.location.href
        });
        // Chiudi il pannello dopo la condivisione
        localStorage.setItem('iosInstallPromptDismissed', 'shared');
        setIsVisible(false);
        setTimeout(() => setShowPrompt(false), 300);
      } catch (error) {
        // Se fallisce o viene cancellato, non fare nulla
        console.log('Share API cancelled or failed:', error);
      }
    }
    // Se non è iOS o Web Share API non è disponibile, il tasto non fa nulla
  };

  const handleNotNow = () => {
    setIsVisible(false);
    setTimeout(() => setShowPrompt(false), 300);
    IOSPromptManager.setNextShow('not-now');
  };

  if (!showPrompt) return null;

  return (
    <>
      {/* Pannello principale */}
      <div 
        className={`position-fixed top-0 start-0 end-0 ${isVisible ? 'translate-y-0' : 'translate-y-n100'}`}
        style={{ 
          zIndex: 1050,
          transform: isVisible ? 'translateY(0)' : 'translateY(-100%)',
          transition: 'transform 0.3s ease-out',
          backgroundColor: 'white',
          boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
        }}
      >
        <div className="container-fluid">
          <div className="row py-4 px-3">
            {/* Icona a sinistra */}
            {showImage && (
              <div className="col-auto">
                <img 
                  src={currentImageSrc}
                  alt="App Icon"
                  style={{ width: '48px', height: '48px' }}
                  className="rounded"
                  onError={(e) => {
                    if (currentImageSrc === config.assets.icon) {
                      // Prima prova con il logo
                      setCurrentImageSrc(config.assets.logo);
                    } else if (currentImageSrc === config.assets.logo) {
                      // Se fallisce anche il logo, nascondi l'immagine
                      setShowImage(false);
                    }
                  }}
                />
              </div>
            )}
            
            {/* Contenuto principale */}
            <div className="col">
              {/* Titolo e descrizioni */}
              <div className="mb-3">
                <h6 className="fw-bold text-dark mb-1">{t('installaApp')}</h6>
                <div className="text-muted small mb-1">{t('installaAppDescrizione')}</div>
                <div className="text-muted small">{t('installaAppDettaglio')}</div>
              </div>
              
              {/* Bottoni allineati a sinistra */}
              <div className="d-flex justify-content-start gap-2">
                <button
                  className="btn btn-primary px-4 py-2"
                  style={{ borderRadius: '25px', fontWeight: '500' }}
                  onClick={handleInstallClick}
                >
                  {t('installa')}
                </button>
                <button
                  className="btn btn-outline-secondary px-3 py-2"
                  style={{ borderRadius: '25px', fontWeight: '500' }}
                  onClick={handleNotNow}
                >
                  {t('nonOra')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default IOSInstallPrompt;